<template>
  <div class="w-full" :key="reRenderKey">
    <div v-if="shownAlert" class="mb-12">
      <zt-alert :color="statusAlert" close-icon filled @afterClosed="shownAlert = false" class="message px-3 py-3">
        <div slot="custom-content">
          <div class="flex justify-start items-center">
            <zt-icon v-if="iconName" :name="iconName" class="icon mr-3"></zt-icon>
            <p>{{ messageAlert }}</p>
          </div>
        </div>
      </zt-alert>
    </div>
    <div class="w-full custom-input mb-10 relative">
      <ValidationProvider ref="fieldPhone" :rules="{ regex: /^\d+$/, min: 3, max: 15 }"
        v-slot="{ errors, failedRules, invalid }">
        <zt-input v-model="params.telephone" class="w-full" :color="invalid ? 'danger' : 'primary'" outlined
          :label="$t('message.selfService.fields.phone')" maxlength="15" data-vs-input="telephone" />
        <validation-message :rules="{ errors, failedRules, invalid, fieldName: 'phone' }" />
      </ValidationProvider>
    </div>
    <div class="w-full custom-input mb-10 relative">
      <ValidationProvider ref="fieldCelular" :rules="{ regex: /^\d+$/, min: 3, max: 15 }"
        v-slot="{ errors, failedRules, invalid }">
        <zt-input v-model="params.telephone_cel" class="w-full" :color="invalid ? 'danger' : 'primary'" outlined
          :label="$t('message.selfService.fields.cellPhone')" maxlength="15" data-vs-input="telephone-cel" />
        <validation-message :rules="{ errors, failedRules, invalid, fieldName: 'celular' }" />
      </ValidationProvider>
    </div>
    <div class="w-full custom-input mb-10 relative">
      <ValidationProvider ref="fieldPhoneEmPeo" :rules="{ regex: /^[a-zA-Z-À-ú-\s-']+$/, min_length: 3, max: 150, no_only_whitespace: true,  no_leading_whitespace: true }"
        v-slot="{ errors, failedRules, invalid }">
        <zt-input v-model="params.name_contact_emergency" class="w-full" :color="invalid ? 'danger' : 'primary'"
          outlined :label="$t('message.selfService.fields.contactPerson')" maxlength="150" @focus="checkFocus"
          data-vs-input="contact-emergency" />
        <validation-message :rules="{ errors, failedRules, invalid, fieldName: 'people' }" />
        <validation-message v-if="isRequiredContactEmergency"
          :rules="{ errors, failedRules: { 'required': true }, invalid: showContactPeopleError, fieldName: 'people' }" />
      </ValidationProvider>
    </div>
    <div class="w-full custom-input mb-10 relative">
      <ValidationProvider ref="fieldTypeContact" :rules="{ regex: /^[a-zA-Z-À-ú-\s-']+$/, min_length: 3, max: 30, no_only_whitespace: true, no_leading_whitespace: true }"
        v-slot="{ errors, failedRules, invalid }">
        <zt-input v-model="params.type_contact" class="w-full" :color="invalid ? 'danger' : 'primary'" outlined
          :label="$t('message.selfService.fields.contactType')" maxlength="30" @focus="checkFocus"
          data-vs-input="type-contact" />
        <validation-message :rules="{ errors, failedRules, invalid, fieldName: 'contactType' }" />
        <validation-message v-if="isRequiredContactType"
          :rules="{ errors, failedRules: { 'required': true }, invalid: showContactTypeError, fieldName: 'contactType' }" />
      </ValidationProvider>
    </div>
    <div class="w-full custom-input mb-10 relative">
      <ValidationProvider ref="fieldPhoneEm" :rules="{ regex: /^\d+$/, min: 3, max: 15 }"
        v-slot="{ errors, failedRules, invalid }">
        <zt-input v-model="params.telephone_emergency" class="w-full" :color="invalid ? 'danger' : 'primary'" outlined
          :label="$t('message.selfService.fields.emergencyPhone')" maxlength="15" data-vs-input="telephone-emergency" />
        <validation-message :rules="{ errors, failedRules, invalid, fieldName: 'phoneEmergence' }" />
      </ValidationProvider>
    </div>
    <div class="flex justify-end w-full">
      <zt-button v-if="pendingApprove" @click="cancelDialog = true" data-vs-button="cancel-request">{{
        $t("message.licenseComponent.licenseBox.cancel") }}</zt-button>
      <zt-button v-else :disabled="isNotAvailable" :loader="isLoading" @click="save" data-vs-button="save">
        {{ $t('message.selfService.actions.save') }}
      </zt-button>
    </div>
    <self-service-cancel-dialog v-if="pendingApprove" :active="cancelDialog" @close-modal="closeModal"
      @confirm="cancelRequest" />
  </div>
</template>

<script>
import Users from '@/apiClient/Users'
import ValidationMessage from '../generic/ValidationMessage'
import SelfServiceCancelDialog from './SelfServiceCancelDialog'
import { isEqual, isEmpty } from 'lodash'
import parseNull from '@/utils/parseNull'
const icons = {
  danger: 'exclamation-circle-solid',
  info: 'info-circle-solid'
}
export default {
  name: 'SelfServiceContactInformation',
  components: { ValidationMessage, SelfServiceCancelDialog },
  props: {
    info: {
      type: Object,
      default: () => ({})
    },
    updateFn: {
      type: Function,
      default: () => { }
    }
  },
  data() {
    return {
      isLoading: false,
      isNotAvailable: true,
      params: {
        telephone: '',
        telephone_cel: '',
        name_contact_emergency: '',
        type_contact: '',
        telephone_emergency: ''
      },
      pendingApprove: false,
      shownAlert: false,
      statusAlert: 'info',
      messageAlert: '',
      cache: {},
      cancelDialog: false,
      cancelRequesting: false,
      reRenderKey: 0,
      showContactPeopleError: false,
      showContactTypeError: false
    }
  },
  watch: {
    params: {
      async handler(newParams) {
        if (this.info.pending && !isEqual(this.cache, this.params)) this.pendingApprove = false
        else this.pendingApprove = this.info.pending
        for (const [key] of Object.entries(newParams)) {
          if (key) {
            const isError = await this.checkFieldError()
            this.isNotAvailable = isError || isEqual(this.cache, this.params)
            if (isError) break
          }
        }
      },
      deep: true
    },
    info() {
      this.infoUpdate()
    }
  },
  computed: {
    iconName() {
      return icons[this.statusAlert]
    },
    isRequiredContactEmergency() {
      return isEmpty(this.params.name_contact_emergency)
    },
    isRequiredContactType() {
      return isEmpty(this.params.type_contact)
    }
  },
  created() {
    this.userApi = new Users()
    this.infoUpdate()
  },
  methods: {
    checkFocus() {
      this.isNotAvailable = isEqual(this.cache, this.params) || this.isRequiredContactEmergency || this.isRequiredContactType
      this.showContactPeopleError = this.isRequiredContactEmergency
      this.showContactTypeError = this.isRequiredContactType
    },
    async save() {
      this.showContactPeopleError = this.isRequiredContactEmergency
      this.showContactTypeError = this.isRequiredContactType
      if (this.isRequiredContactEmergency || this.isRequiredContactType) return
      if (this.isNotAvailable) return
      this.isLoading = true
      this.shownAlert = false
      const body = this.params
      body.name_contact_emergency = this.params.name_contact_emergency ? this.params.name_contact_emergency.trim() : ''
      body.type_contact = this.params.type_contact ? this.params.type_contact.trim() : ''
      if (body.id) delete body.id
      if ('sichernpeople' in body) delete body.sichernpeople
      try {
        await this.userApi.saveContactInfo(body)
        await this.updateFn()
        this.isLoading = false
        this.isNotAvailable = true
        this.shownAlert = true
        this.statusAlert = 'info'
        this.messageAlert = this.$t('message.selfService.response.success')
      } catch (e) {
        const code = e.response.data.codes[0].code
        this.messageAlert = this.$t(
          `message.selfService.response.errors.${code}`
        )
        this.isLoading = false
        this.isNotAvailable = false
        this.shownAlert = true
        this.statusAlert = 'danger'
      }
    },
    async checkFieldError() {
      for (const [, provider] of Object.entries(this.$refs)) {
        if (provider) {
          const { valid } = await provider.validate()
          if (!valid) return true
        }
      }
      return false
    },
    infoUpdate() {
      const { pending, ...rest } = this.info
      this.cache = parseNull({ ...rest })
      this.params = parseNull({ ...rest })
      this.pendingApprove = pending
      this.messageAlert = this.$t('message.selfService.response.success')
      this.statusAlert = 'info'
      this.shownAlert = pending
      this.reRenderKey += 1
    },
    async cancelRequest() {
      this.cancelRequesting = true
      try {
        await this.userApi.cancelSelfServiceRequest(this.info.id)
        await this.updateFn()
        this.cancelRequesting = false
        this.closeModal()
        this.messageAlert = this.$t('message.selfService.response.successCancel')
        this.statusAlert = 'info'
        this.shownAlert = true
      } catch (e) {
        if (e.response.status === 404) {
          this.messageAlert = this.$t(
            `message.selfService.response.errors.notFound`
          )
        } else {
          const code = e.response.data.codes[0].code
          this.messageAlert = this.$t(
            `message.selfService.response.errors.${code}`
          )
        }
        this.isLoading = false
        this.isNotAvailable = false
        this.shownAlert = true
        this.statusAlert = 'danger'
        this.cancelRequesting = false
        this.closeModal()
      }
    },
    closeModal() {
      if (this.cancelRequesting) return
      this.cancelRequesting = false
      this.cancelDialog = false
    }
  }
}
</script>

<style scoped lang="scss">
.custom-input {
  ::v-deep {
    .zt-label.zt-label-active {
      background: #fff !important;
      padding: 2px !important;
    }

    .zt-input {
      font-size: 1.125rem;
    }
  }
}
</style>
